import {InputLabel, Stack, SxProps, Theme} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers-pro';
import {useRef, useState} from 'react';

import {Calendar} from '@icons/index';
import {unstable_useEnhancedEffect as useEnhancedEffect} from '@mui/utils';
import dayjs from '@utils/dayjs.utils';
import {Colors} from '@constants/colors.constants';

interface DatePickerComponentProps {
  color?: 'primary' | 'secondary';
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  format?: string;
  handleChange?: (date?: Date) => void;
  label?: string;
  labelWidth?: number;
  inputWidth?: number;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
  preventClick?: boolean;
  readOnly?: boolean;
  required?: boolean;
  size?: 'small';
  error?: string;
  value?: Date;
  hasFocus?: boolean;
  fontStyle?: SxProps<Theme>;
  inputStyle?: object;
  calendarStyle?: object;
  inputBaseStyle?: object;
}

const DatePickerComponent = (props: DatePickerComponentProps) => {
  const {
    color = 'primary',
    disabled,
    handleChange,
    label,
    labelWidth,
    inputWidth,
    maxDate,
    minDate,
    placeholder,
    preventClick,
    readOnly,
    required,
    size,
    error,
    value,
    hasFocus,
    fontStyle,
    inputStyle,
    calendarStyle,
    inputBaseStyle,
  } = props;

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEnhancedEffect(() => {
    if (hasFocus && ref.current && !readOnly) {
      const input = ref.current.querySelector<HTMLInputElement>(
        'input',
      );
      input?.focus();
    }
  }, [hasFocus, value, readOnly]);

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      {!!label && (
        <InputLabel error={!!error && !disabled} sx={{width: labelWidth ? `${labelWidth}px` : undefined, ...fontStyle}}>
          {label + (required ? '*' : '')}
        </InputLabel>
      )}
      <DatePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        ref={ref}
        shouldDisableDate={(day) => {
          if (minDate) {
            return dayjs(day).isSameOrBefore(minDate, 'd');
          }
          if (maxDate) {
            return dayjs(day).isSameOrAfter(maxDate, 'd');
          }
          return false;

        }}
        disabled={disabled}
        readOnly={readOnly}
        value={value ? dayjs(value) : null}
        format={'DD.MM.YY'}
        sx={{
          '& .MuiFilledInput': {
            padding: '0px 0px !important',
          },
        }}
        onChange={(date) => {
          if (!date || (!date.isSame(value) && date.isValid())) {
            handleChange?.(date?.toDate() || undefined);
          }
        }}
        slots={{
          openPickerIcon: Calendar,
        }}
        slotProps={{
          popper: {
            style: {zIndex: 1301},
          },
          inputAdornment: {
            position: 'start',
            sx: {ml: '8px !important'},
          },
          openPickerIcon: {
            tabIndex: -1
          },
          openPickerButton: {
            tabIndex: -1
          },
          textField: {
            sx: {
              flex: 1,
              width: inputWidth ? `${inputWidth}px` : undefined,
              '& .MuiInputBase-input': {
                ...inputBaseStyle,
              },
              '& .MuiTextField-root': {
                padding: '0px 0px !important',
              },
              '& .MuiFilledInput-root': {
                backgroundColor: color === 'primary' ? 'white' : undefined,
                '&:hover': {
                  backgroundColor: color === 'primary' ? 'white' : undefined,
                },
                '&.Mui-focused': {
                  backgroundColor: color === 'primary' ? 'white' : undefined,
                },
                ...inputStyle,
              },
              '& .MuiInputAdornment-root': {
                ...calendarStyle
              }
            },
            size: size,
            disabled: disabled,
            onClick: (evt) => preventClick ? evt.stopPropagation() : null,
            focused: readOnly ? false : undefined,
            inputProps: {
              placeholder: placeholder,
              style: {backgroundColor: color == 'secondary' ? Colors.background : 'white'},
            },
            error: !!error,
            helperText: error,
          }
        }}
      />
    </Stack>
  );
};

export default DatePickerComponent;
